/*
 * Redirect URL Extraction for Ory Flow Responses
 *
 * Handles multiple Ory redirect formats and constructs proper API endpoints
 * for flow retrieval when Ory returns flow IDs instead of complete URLs.
 *
 * Supported formats:
 * 1. `redirect_browser_to` - Direct URL (older format)
 * 2. `error.details.redirect_to` - Nested URL (newer format)
 * 3. `use_flow_id` - Flow ID only (newest format, requires URL construction)
 * 4. `error.details.continue_with[].flow.url` - Continue-with actions (e.g., verification required)
 */
import type { OryErrorBrowserRedirect, OryErrorContinueWith, OryContinueWithAction } from '../model';

/**
 * Extracts redirect URL from Ory redirect response.
 *
 * @remarks
 * Handles three Ory redirect formats in priority order:
 * 1. `redirect_browser_to` - Direct URL (older format)
 * 2. `error.details.redirect_to` - Nested URL (newer format)
 * 3. `use_flow_id` - Flow ID only (newest format, requires URL construction)
 *
 * When `use_flow_id` is present, delegates to `constructRedirectUrlFromFlowId`
 * to build the proper Ory API endpoint URL.
 *
 * @param data - Ory API response containing redirect information
 * @param requestUrl - Original request URL (used for `use_flow_id` construction)
 * @returns Redirect URL string, or `undefined` if no redirect format present
 */
export const extractRedirectUrl = (data: OryErrorBrowserRedirect, requestUrl: string): string | undefined => {
  // Check top-level redirect_browser_to first (older format)
  if (data.redirect_browser_to) {
    return data.redirect_browser_to;
  }

  // Check nested error.details.redirect_to (newer format)
  if (data.error?.details?.redirect_to) {
    return data.error.details.redirect_to;
  }

  // Check use_flow_id (newest format)
  if (data.use_flow_id) {
    return constructRedirectUrlFromFlowId(data.use_flow_id, requestUrl);
  }

  return undefined;
};

/**
 * Constructs redirect URL from flow ID and request context.
 *
 * @remarks
 * When Ory returns `use_flow_id`, constructs URL to retrieve and display
 * the new flow by extracting flow type from original request URL.
 *
 * Uses Ory's documented `/flows?id=` endpoint for fetching flow data.
 * This ensures SPA navigation per `#classifyRedirect` business rule in auth.service.
 *
 * Fallback defaults to login flow when URL parsing fails (should never occur
 * with valid Ory URLs, logs error if triggered).
 *
 * @param flowId - New flow ID from Ory's `use_flow_id` response field
 * @param requestUrl - Original request URL that triggered the flow (from axios error.config.url)
 * @returns Ory API URL to fetch flow data: `/self-service/{flowType}/flows?id={flowId}`
 *
 * @example
 * ```typescript
 * // Flow expiration during login
 * const url = constructRedirectUrlFromFlowId('new-id-123', '/self-service/login/browser');
 * // Returns: '/self-service/login/flows?id=new-id-123'
 * ```
 *
 * @example
 * ```typescript
 * // Flow expiration during settings with existing flow param
 * const url = constructRedirectUrlFromFlowId('new-id-456', '/self-service/settings?flow=old-id');
 * // Returns: '/self-service/settings/flows?id=new-id-456'
 * ```
 *
 * @see {@link https://www.ory.com/docs/kratos/bring-your-own-ui/custom-ui-basic-integration#fetch-existing-flows | Ory Fetch Existing Flows}
 */
export const constructRedirectUrlFromFlowId = (flowId: string, requestUrl: string): string => {
  // Try to extract flow type from request URL
  // Patterns to match:
  // - /self-service/{flowType}/browser
  // - /{flowType}

  const selfServiceMatch = requestUrl.match(/\/self-service\/([^/?]+)/);
  if (selfServiceMatch) {
    const flowType = selfServiceMatch[1];
    // Return Ory API format: /self-service/{flowType}/flows?id={flowId}
    // Application layer is responsible for transforming this to SPA routes
    return `/self-service/${flowType}/flows?id=${flowId}`;
  }

  // Fallback: Default to login flow as safest assumption
  // Provides working redirect even if URL parsing fails
  console.error(
    `Cannot determine flow type from URL: ${requestUrl}. Defaulting to login flow.`,
    { flowId, requestUrl }
  );
  return `/self-service/login/flows?id=${flowId}`;
};

/**
 * Actions in continue_with that require browser navigation.
 * Must match CONTINUE_WITH_REDIRECT_ACTIONS in response-guards.ts.
 */
const CONTINUE_WITH_REDIRECT_ACTIONS: OryContinueWithAction[] = [
  'show_verification_ui',
  'show_settings_ui',
  'show_recovery_ui',
];

/**
 * Extracts flow URL from Ory error response with continue_with actions.
 *
 * @remarks
 * When Ory returns an error envelope with `details.continue_with` containing
 * show_*_ui actions, extracts the flow URL for browser navigation.
 *
 * Priority order matches Ory's semantic intent:
 * 1. show_verification_ui - Email/phone verification required
 * 2. show_settings_ui - Settings update required
 * 3. show_recovery_ui - Recovery flow continuation
 *
 * @param data - Ory error envelope with continue_with actions
 * @returns Flow URL for browser navigation, or undefined if not found
 *
 * @example
 * ```typescript
 * // Extract verification URL from session_verified_address_required error
 * const response = {
 *   error: {
 *     id: 'session_verified_address_required',
 *     details: {
 *       continue_with: [{
 *         action: 'show_verification_ui',
 *         flow: {
 *           id: '5aa47224-ce60-4be9-8c8b-62128a22fadd',
 *           url: 'http://localhost:4100/verification?flow=5aa47224-ce60-4be9-8c8b-62128a22fadd'
 *         }
 *       }]
 *     }
 *   }
 * };
 *
 * const url = extractContinueWithFlowUrl(response);
 * // Returns: 'http://localhost:4100/verification?flow=5aa47224-ce60-4be9-8c8b-62128a22fadd'
 * ```
 */
export const extractContinueWithFlowUrl = (data: OryErrorContinueWith): string | undefined => {
  const continueWith = data.error?.details?.continue_with;

  if (!Array.isArray(continueWith)) {
    return undefined;
  }

  // Find first action that requires redirect and has a valid URL
  for (const action of continueWith) {
    if (
      action &&
      typeof action === 'object' &&
      'action' in action &&
      CONTINUE_WITH_REDIRECT_ACTIONS.includes(action.action as OryContinueWithAction) &&
      'flow' in action &&
      action.flow &&
      typeof action.flow === 'object' &&
      'url' in action.flow &&
      typeof action.flow.url === 'string' &&
      action.flow.url.length > 0
    ) {
      return action.flow.url;
    }
  }

  return undefined;
};

